import type { ReactNode } from "react"
import { useEffect, useMemo, useRef } from "react"

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select"
import { DAILY_CHECKIN_LABELS, DAILY_CHECKIN_REQUIRED_KEYS } from "constants/dailyCheckin"
import type { LifeScoresReport } from "types"
import type { Person } from "./types"

const MIN_SCORE = 0
const MAX_SCORE = 10
const RAW_MIN_SCORE = 1
const RAW_MAX_SCORE = 7

const toDisplayScore = (value: number | null) => {
  if (typeof value !== "number") return null
  const clamped = Math.max(RAW_MIN_SCORE, Math.min(value, RAW_MAX_SCORE))
  return ((clamped - RAW_MIN_SCORE) / (RAW_MAX_SCORE - RAW_MIN_SCORE)) * (MAX_SCORE - MIN_SCORE) + MIN_SCORE
}

const SERIES_COLORS: Record<string, { stroke: string; fill: string; bg: string }> = {
  overall: { stroke: "text-rose-500", fill: "fill-rose-500", bg: "bg-rose-500" },
  DC_EFFORT: { stroke: "text-amber-500", fill: "fill-amber-500", bg: "bg-amber-500" },
  DC_PHYSICAL: { stroke: "text-blue-600", fill: "fill-blue-600", bg: "bg-blue-600" },
  DC_PSYCHOLOGICAL: { stroke: "text-emerald-500", fill: "fill-emerald-500", bg: "bg-emerald-500" },
  DC_WORK: { stroke: "text-fuchsia-600", fill: "fill-fuchsia-600", bg: "bg-fuchsia-600" },
}

type LineSeries = {
  key: string
  label: string
  strokeClass: string
  fillClass: string
  legendClass: string
  values: Array<number | null>
  dashed?: boolean
}

const parseIsoDate = (value: string) => {
  const parts = value.split("-").map((part) => Number(part))
  if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
    return null
  }
  const [year, month, day] = parts
  const date = new Date(year, month - 1, day)
  return Number.isNaN(date.getTime()) ? null : date
}

const formatIsoDate = (value: Date) => {
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, "0")
  const day = String(value.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

const toStartDateTimestamp = (value: string) => {
  const date = parseIsoDate(value)
  return date ? date.getTime() : Number.NEGATIVE_INFINITY
}

const formatBucketDateLabel = (value: string) => {
  const date = parseIsoDate(value)
  if (!date) {
    return value
  }
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
}

const buildPath = (values: Array<number | null>, width: number, height: number) => {
  const points: Array<{ x: number; y: number }> = []
  const step = values.length > 1 ? width / (values.length - 1) : 0
  values.forEach((value, idx) => {
    if (typeof value !== "number") return
    const clamped = Math.max(MIN_SCORE, Math.min(value, MAX_SCORE))
    const x = idx * step
    const y = height - ((clamped - MIN_SCORE) / (MAX_SCORE - MIN_SCORE)) * height
    points.push({ x, y })
  })
  if (points.length === 0) return ""
  return points.map((point, index) => `${index === 0 ? "M" : "L"}${point.x},${point.y}`).join(" ")
}

type ReportsTrendsCardProps = {
  data?: LifeScoresReport | null
  loading?: boolean
  error?: string | null
  roster?: Person[]
  selectedUserId?: string
  onUserChange?: (value: string) => void
  title?: ReactNode
  mobileEyebrow?: ReactNode
  mobileTitle?: ReactNode
  emptyStateText?: string
  className?: string
}

const COHORT_SERIES_COLORS: Record<string, { stroke: string; fill: string; bg: string }> = {
  overall: { stroke: "text-[var(--cohort-action)]", fill: "fill-[var(--cohort-action)]", bg: "bg-[var(--cohort-action)]" },
  DC_EFFORT: { stroke: "text-[var(--cohort-orange)]", fill: "fill-[var(--cohort-orange)]", bg: "bg-[var(--cohort-orange)]" },
  DC_PHYSICAL: { stroke: "text-[var(--cohort-sage)]", fill: "fill-[var(--cohort-sage)]", bg: "bg-[var(--cohort-sage)]" },
  DC_PSYCHOLOGICAL: { stroke: "text-[var(--cohort-ochre)]", fill: "fill-[var(--cohort-ochre)]", bg: "bg-[var(--cohort-ochre)]" },
  DC_WORK: { stroke: "text-[var(--cohort-clay)]", fill: "fill-[var(--cohort-clay)]", bg: "bg-[var(--cohort-clay)]" },
}

type ReportsTrendsChartContentProps = {
  data?: LifeScoresReport | null
  loading?: boolean
  error?: string | null
  selectedUserId?: string
  emptyStateText?: string
  cohortPalette?: boolean
}

export function ReportsTrendsChartContent({
  data,
  loading,
  error,
  selectedUserId = "all",
  emptyStateText = "No life score data available yet.",
  cohortPalette = false,
}: ReportsTrendsChartContentProps) {
  const chartScrollRef = useRef<HTMLDivElement | null>(null)

  const buckets = useMemo(() => {
    const source = data?.buckets ?? []
    const sorted = [...source].sort(
      (left, right) =>
        toStartDateTimestamp(left.start_date) - toStartDateTimestamp(right.start_date),
    )
    if (!sorted.length) return sorted

    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const bucketSizeDays = Math.max(1, data?.bucket_size_days ?? 7)
    const dimensionKeys = Object.keys(sorted[0].cohort?.dimensions ?? {})
    const createEmptyDimensions = () =>
      dimensionKeys.reduce<Record<string, number | null>>((acc, key) => {
        acc[key] = null
        return acc
      }, {})

    const lastKnownEnd = parseIsoDate(sorted[sorted.length - 1].end_date)
    if (!lastKnownEnd) return sorted

    const todayTimestamp = today.getTime()
    let lastEndTimestamp = lastKnownEnd.getTime()

    while (lastEndTimestamp < todayTimestamp) {
      const nextStart = new Date(lastEndTimestamp)
      nextStart.setDate(nextStart.getDate() + 1)

      const nextEnd = new Date(nextStart)
      nextEnd.setDate(nextEnd.getDate() + bucketSizeDays - 1)
      if (nextEnd.getTime() > todayTimestamp) {
        nextEnd.setTime(todayTimestamp)
      }

      sorted.push({
        start_date: formatIsoDate(nextStart),
        end_date: formatIsoDate(nextEnd),
        cohort: {
          overall: null,
          dimensions: createEmptyDimensions(),
        },
        user: null,
      })
      lastEndTimestamp = nextEnd.getTime()
    }

    return sorted
  }, [data?.bucket_size_days, data?.buckets])

  const bucketLabels = useMemo(() => {
    return buckets.map((bucket) => ({
      key: bucket.start_date,
      label: formatBucketDateLabel(bucket.start_date),
    }))
  }, [buckets])

  const series = useMemo<LineSeries[]>(() => {
    const seriesColors = cohortPalette ? COHORT_SERIES_COLORS : SERIES_COLORS
    if (selectedUserId !== "all") {
      const userOverall: LineSeries = {
        label: "Overall",
        key: "user-overall",
        strokeClass: seriesColors.overall.stroke,
        fillClass: seriesColors.overall.fill,
        legendClass: seriesColors.overall.bg,
        values: buckets.map((bucket) => toDisplayScore(bucket.user?.overall ?? null)),
      }
      const userDimensions = DAILY_CHECKIN_REQUIRED_KEYS.map((key) => ({
        label: DAILY_CHECKIN_LABELS[key],
        key: `user-${key}`,
        strokeClass: seriesColors[key]?.stroke || "text-gray-400",
        fillClass: seriesColors[key]?.fill || "fill-gray-400",
        legendClass: seriesColors[key]?.bg || "bg-gray-400",
        values: buckets.map((bucket) => toDisplayScore(bucket.user?.dimensions?.[key] ?? null)),
      }))
      return [userOverall, ...userDimensions]
    }

    const overall: LineSeries = {
      label: "Overall",
      key: "cohort-overall",
      strokeClass: seriesColors.overall.stroke,
      fillClass: seriesColors.overall.fill,
      legendClass: seriesColors.overall.bg,
      values: buckets.map((bucket) => toDisplayScore(bucket.cohort?.overall ?? null)),
    }

    const dimensions = DAILY_CHECKIN_REQUIRED_KEYS.map((key) => ({
      label: DAILY_CHECKIN_LABELS[key],
      key: `cohort-${key}`,
      strokeClass: seriesColors[key]?.stroke || "text-gray-400",
      fillClass: seriesColors[key]?.fill || "fill-gray-400",
      legendClass: seriesColors[key]?.bg || "bg-gray-400",
      values: buckets.map((bucket) => toDisplayScore(bucket.cohort?.dimensions?.[key] ?? null)),
    }))

    return [overall, ...dimensions]
  }, [buckets, cohortPalette, selectedUserId])

  const hasAnyData = useMemo(
    () => series.some((row) => row.values.some((value) => typeof value === "number")),
    [series],
  )
  const plotWidth = Math.max(260, (bucketLabels.length - 1) * 70)
  const plotHeight = 200
  const leftPadding = 30
  const rightPadding = 20
  const topPadding = 20
  const bottomPadding = 60
  const svgWidth = leftPadding + plotWidth + rightPadding
  const svgHeight = topPadding + plotHeight + bottomPadding

  useEffect(() => {
    const container = chartScrollRef.current
    if (!container) return
    const frame = requestAnimationFrame(() => {
      container.scrollLeft = Math.max(0, container.scrollWidth - container.clientWidth)
    })
    return () => cancelAnimationFrame(frame)
  }, [bucketLabels.length, selectedUserId])

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading life score trends...</p>
  }

  if (error) {
    return <p className="text-sm text-destructive">{error}</p>
  }

  if (buckets.length === 0 || !hasAnyData) {
    return <p className="text-sm text-muted-foreground">{emptyStateText}</p>
  }

  return (
    <div className="space-y-4 w-full">
      <div className={`space-y-3 rounded-xl border bg-white p-4 ${cohortPalette ? "border-[var(--cohort-rule)]" : "border-gray-200"}`}>
        <div className={`grid gap-2 text-xs md:grid-cols-3 ${cohortPalette ? "text-[var(--cohort-ink-muted)]" : "text-gray-600"}`}>
          {series.map((row) => (
            <div key={`${row.key}-legend`} className="flex items-center gap-2">
              <span className={`h-2.5 w-2.5 rounded-full ${row.legendClass}`} />
              <span>{row.label}</span>
            </div>
          ))}
        </div>
        <div ref={chartScrollRef} className="w-full overflow-x-auto">
          <svg
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            className="min-w-[18.75rem]"
            style={{ width: Math.max(svgWidth, 300) }}
          >
            <g transform={`translate(${leftPadding} ${topPadding})`}>
              <rect
                x={0}
                y={0}
                width={plotWidth}
                height={plotHeight}
                rx={12}
                fill={cohortPalette ? "var(--cohort-rest)" : "#f8fafc"}
              />
              {(() => {
                const ticks = [0, 2, 4, 6, 8, 10]
                return ticks.map((tick) => {
                  const y = plotHeight - ((tick - MIN_SCORE) / (MAX_SCORE - MIN_SCORE)) * plotHeight
                  return (
                    <g key={`tick-${tick}`}>
                      <line
                        x1={0}
                        x2={plotWidth}
                        y1={y}
                        y2={y}
                        stroke={cohortPalette ? "var(--cohort-rule-soft)" : "#e2e8f0"}
                        strokeDasharray="4 4"
                      />
                      <text
                        x={-10}
                        y={y + 3}
                        textAnchor="end"
                        className={`${cohortPalette ? "fill-[var(--cohort-ink-subtle)]" : "fill-gray-500"} text-[10px]`}
                      >
                        {tick}
                      </text>
                    </g>
                  )
                })
              })()}
              {series.map((row) => {
                const path = buildPath(row.values, plotWidth, plotHeight)
                if (!path) return null
                return (
                  <g key={`path-${row.key}`}>
                    <path
                      d={path}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className={row.strokeClass}
                      strokeDasharray={row.dashed ? "6 4" : "0"}
                    />
                    {row.values.map((value, idx) => {
                      if (typeof value !== "number") return null
                      const xStep = row.values.length > 1 ? plotWidth / (row.values.length - 1) : 0
                      const clamped = Math.max(MIN_SCORE, Math.min(value, MAX_SCORE))
                      const y = plotHeight - ((clamped - MIN_SCORE) / (MAX_SCORE - MIN_SCORE)) * plotHeight
                      const x = idx * xStep
                      return (
                        <g key={`${row.key}-dot-${idx}`}>
                          <circle
                            cx={x}
                            cy={y}
                            r={4}
                            className={row.fillClass}
                          />
                          <text
                            x={x}
                            y={y - 8}
                            textAnchor="middle"
                            className={`${cohortPalette ? "fill-[var(--cohort-ink)]" : "fill-gray-700"} text-[10px]`}
                          >
                            {value.toFixed(1)}
                          </text>
                        </g>
                      )
                    })}
                  </g>
                )
              })}
              {bucketLabels.map((label, idx) => {
                const xStep = bucketLabels.length > 1 ? plotWidth / (bucketLabels.length - 1) : 0
                const x = idx * xStep
                return (
                  <text
                    key={`label-${label.key}-${idx}`}
                    x={x}
                    y={plotHeight + 20}
                    textAnchor="middle"
                    className={`${cohortPalette ? "fill-[var(--cohort-ink-subtle)]" : "fill-gray-500"} text-[10px]`}
                  >
                    {label.label}
                  </text>
                )
              })}
            </g>
          </svg>
        </div>
      </div>
    </div>
  )
}

export function ReportsTrendsCard({
  data,
  loading,
  error,
  roster = [],
  selectedUserId = "all",
  onUserChange,
  title,
  mobileEyebrow,
  mobileTitle,
  emptyStateText = "No life score data available yet.",
  className = "",
}: ReportsTrendsCardProps) {
  const selectableRoster = roster.filter(
    (person) => person.roleKey === "mentee" || person.roleKey === "mentor",
  )

  return (
    <section className={`cohort-open-section ${className}`} aria-labelledby="reports-trends-title">
      {mobileEyebrow ? <p className="cohort-section-eyebrow lg:hidden">{mobileEyebrow}</p> : null}
      <div className="flex flex-col gap-3 border-b border-[var(--cohort-rule)] pb-2.5 sm:flex-row sm:items-center sm:justify-between">
        <h2 id="reports-trends-title" className={`text-[var(--cohort-ink)] ${mobileTitle ? "text-[21px] lg:text-xl" : "text-[19px] sm:text-xl"}`}>
          {mobileTitle ? <><span className="lg:hidden">{mobileTitle}</span><span className="hidden lg:inline">{title ?? "Reports & trends"}</span></> : title ?? "Reports & trends"}
        </h2>
          {onUserChange ? (
            <Select value={selectedUserId} onValueChange={onUserChange}>
              <SelectTrigger className="cohort-secondary-button min-h-10 w-full rounded-[10px] text-sm sm:w-[220px]">
                <SelectValue placeholder="All cohort members" />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="all">All cohort members</SelectItem>
                {selectableRoster.map((person) => (
                  <SelectItem key={person.id} value={String(person.id)}>
                    {person.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
      </div>
      <div className="mt-5">
        <ReportsTrendsChartContent
          data={data}
          loading={loading}
          error={error}
          selectedUserId={selectedUserId}
          emptyStateText={emptyStateText}
          cohortPalette
        />
      </div>
    </section>
  )
}
